import { supabase } from './supabase'
import { validateIban, formatIban } from './validators/iban'
import { parseAmountToCents } from './money'
import {
  validateStructuredCommunication,
  formatReference,
  normaliseReference,
} from './validators/structuredCommunication'

/**
 * Verification: turning what the AI read into something a human has stood behind.
 *
 * Two rules shape all of this.
 *
 * Rule 6 — extractions are append-only. A correction never overwrites what the
 * model said; it writes a NEW row marked produced_by='human'. That is the only
 * way to answer "is extraction getting better or worse" months later.
 *
 * The obligation's check constraint (0001) refuses to store an IBAN unless
 * verified_at is set. So payment details literally cannot exist in the
 * database until this screen has been through.
 */

export interface VerifiableField {
  name: string
  value: string
  confidence: number
  page: number
  bbox: [number, number, number, number] | null
  validation: 'pass' | 'fail' | 'skip'
  validationNote?: string | null
}

export interface VerificationSubject {
  documentId: string
  obligationId: string | null
  spaceId: string
  /** Resolved from supplier history; correctable here and nowhere else. */
  category: string | null
  lane: 'green' | 'blue' | 'amber' | 'red' | null
  reasons: string[]
  fields: VerifiableField[]
  extractionId: string
  modelVersion: string
  /** IBANs this supplier has used before, for the "same account" line. */
  knownIbans: string[]
  supplierName: string | null
  partyId: string | null
}

/** Human-facing labels. The admin should never see a field key. */
export const FIELD_LABELS: Record<string, string> = {
  supplier_name: 'Leverancier',
  invoice_number: 'Factuurnummer',
  invoice_date: 'Factuurdatum',
  due_date: 'Vervaldatum',
  total_amount: 'Totaalbedrag',
  net_amount: 'Excl. btw',
  vat_amount: 'Btw',
  iban: 'Rekeningnummer',
  payment_reference: 'Mededeling',
}

/** Order shown on screen: what matters most for paying, first. */
const FIELD_ORDER = [
  'supplier_name',
  'total_amount',
  'due_date',
  'iban',
  'payment_reference',
  'invoice_number',
  'invoice_date',
  'net_amount',
  'vat_amount',
]

export async function loadVerification(documentId: string): Promise<VerificationSubject | null> {
  const { data: extraction, error } = await supabase
    .from('extractions')
    .select('id, space_id, lane, lane_reasons, fields, model_version')
    .eq('document_id', documentId)
    .order('created_at', { ascending: false })
    .limit(1)
    .maybeSingle()
  if (error) throw error
  if (!extraction) return null

  const { data: obligation } = await supabase
    .from('obligations')
    .select('id, party_id, space_id, category')
    .eq('source_document_id', documentId)
    .is('deleted_at', null)
    .limit(1)

  const ob = obligation?.[0]

  let knownIbans: string[] = []
  let supplierName: string | null = null
  if (ob?.party_id) {
    const { data: party } = await supabase
      .from('parties')
      .select('name, pinned_ibans')
      .eq('id', ob!.party_id)
      .maybeSingle()
    supplierName = (party?.name as string | undefined) ?? null
    knownIbans = ((party?.pinned_ibans ?? []) as { iban: string }[]).map((p) => p.iban)
  }

  const raw = (extraction.fields ?? []) as VerifiableField[]
  const fields = [...raw].sort(
    (a, b) => indexOf(a.name) - indexOf(b.name),
  )

  return {
    documentId,
    obligationId: (ob?.id as string | undefined) ?? null,
    // The obligation's space is the routed one and wins; the extraction's is
    // merely where the photo happened to land.
    spaceId: (ob?.space_id as string | undefined) ?? (extraction.space_id as string),
    category: (ob?.category as string | null | undefined) ?? null,
    lane: extraction.lane as VerificationSubject['lane'],
    reasons: (extraction.lane_reasons ?? []) as string[],
    fields,
    extractionId: extraction.id as string,
    modelVersion: extraction.model_version as string,
    knownIbans,
    supplierName,
    partyId: (ob?.party_id as string | undefined) ?? null,
  }
}

function indexOf(name: string): number {
  const i = FIELD_ORDER.indexOf(name)
  return i === -1 ? FIELD_ORDER.length : i
}

/** Re-run the deterministic checks on a value the human just typed. */
export function validateValue(name: string, value: string): { ok: boolean; note?: string } {
  if (name === 'iban') {
    const r = validateIban(value)
    return r.valid ? { ok: true } : { ok: false, note: describeIbanFailure(r.reason) }
  }
  if (name === 'payment_reference') {
    if (!value.trim()) return { ok: true }
    const r = validateStructuredCommunication(value)
    return r.valid
      ? { ok: true }
      : { ok: false, note: 'Deze mededeling klopt niet — controleer de cijfers.' }
  }
  if (name === 'total_amount' || name === 'net_amount' || name === 'vat_amount') {
    // Through the money parser, not a regex of its own.
    //
    // The regex here was `\d+([.,]\d{1,2})?`, which rejects "1.289,70" — a
    // thousands separator followed by two decimals, which is what every
    // Belgian invoice over a thousand euro looks like. It put a red "give an
    // amount" under a perfectly good number, and would have pushed every
    // four-figure invoice through the long screen.
    //
    // `parseAmountToCents` already knows that 1.289,70 and 1,289.70 are the
    // same money, and it is the function the rest of the app pays people with.
    // Two opinions about what a number is, is one too many.
    const cents = parseAmountToCents(value)
    return cents !== null && cents >= 0
      ? { ok: true }
      : { ok: false, note: 'Geef een bedrag, bijvoorbeeld 1.286,40' }
  }
  if (name.endsWith('_date')) {
    return /^\d{4}-\d{2}-\d{2}$/.test(value)
      ? { ok: true }
      : { ok: false, note: 'Gebruik JJJJ-MM-DD' }
  }
  return { ok: true }
}

function describeIbanFailure(reason: string): string {
  switch (reason) {
    case 'checksum_failed':
      return 'Dit rekeningnummer bestaat niet — er is een cijfer verkeerd.'
    case 'wrong_length_for_country':
      return 'Verkeerde lengte voor dit land.'
    case 'empty':
      return 'Vul een rekeningnummer in.'
    default:
      return 'Dit lijkt geen geldig rekeningnummer.'
  }
}

export function prettyValue(name: string, value: string): string {
  if (name === 'iban') {
    const r = validateIban(value)
    return r.valid ? formatIban(r.canonical) : value
  }
  if (name === 'payment_reference' && normaliseReference(value).length === 12) {
    return formatReference(value)
  }
  return value
}

/**
 * Accept the reading, with any corrections the human made.
 *
 * Writes a new append-only extraction row, then — and only then — writes the
 * payment details onto the obligation with verified_at set. Until this runs,
 * no QR code and no payment card can exist for the item.
 */
/**
 * Accept a reading exactly as it stands, without opening it.
 *
 * The triage screen's "Klopt" used to advance the card and nothing else. It
 * looked like it had worked — the card slid away — and recorded nothing, so
 * everything came back the next morning and none of it could enter a payment
 * run (rule 15). A button that lies about having done something is worse than
 * one that is missing, because you stop checking.
 *
 * It refuses whatever it is not entitled to accept. Anything red, any changed
 * IBAN, any estimate: those need the full screen, and the caller is told so
 * rather than being quietly given a yes.
 */
export async function quickConfirm(
  documentId: string,
  userId: string,
): Promise<{ ok: true } | { ok: false; reason: string }> {
  const subject = await loadVerification(documentId)
  if (!subject) return { ok: false, reason: 'Dit is nog niet gelezen.' }

  if (subject.lane === 'red') {
    return { ok: false, reason: 'Hier klopt iets niet — kijk het na.' }
  }

  const iban = validateIban(valueOfField(subject, 'iban'))
  if (
    iban.valid &&
    subject.knownIbans.length > 0 &&
    !subject.knownIbans.includes(iban.canonical)
  ) {
    return { ok: false, reason: 'Het rekeningnummer is veranderd — kijk het na.' }
  }

  const broken = subject.fields.find((f) => !validateValue(f.name, f.value).ok)
  if (broken) {
    return { ok: false, reason: `${FIELD_LABELS[broken.name] ?? broken.name} klopt niet.` }
  }

  await confirmVerification({ subject, corrections: {}, userId })
  return { ok: true }
}

function valueOfField(subject: VerificationSubject, name: string): string {
  return subject.fields.find((f) => f.name === name)?.value ?? ''
}

export async function confirmVerification(params: {
  subject: VerificationSubject
  corrections: Record<string, string>
  userId: string
  /** True when the human confirmed a changed IBAN by phoning the supplier. */
  ibanChangeAcknowledged?: boolean
  /** Correcting where this belongs, and what kind of cost it is. */
  routing?: { spaceId?: string; category?: string | null }
}): Promise<void> {
  // `userId` is no longer read here: the database takes auth.uid(), which is
  // the one version of that answer a browser cannot misstate. It stays in the
  // signature so callers did not all have to change on the same day.
  const { subject, corrections, ibanChangeAcknowledged = false, routing } = params

  const finalFields = subject.fields.map((f) => {
    const corrected = corrections[f.name]
    if (corrected === undefined || corrected === f.value) return f
    return {
      ...f,
      value: corrected,
      confidence: 1,
      corrected_from: f.value,
      validation: validateValue(f.name, corrected).ok ? 'pass' : 'fail',
    }
  })

  const changed = Object.keys(corrections).filter(
    (k) => corrections[k] !== subject.fields.find((f) => f.name === k)?.value,
  )

  const value = (name: string) =>
    corrections[name] ?? subject.fields.find((f) => f.name === name)?.value ?? ''

  const ibanCheck = validateIban(value('iban'))
  const reference = value('payment_reference')
  const refCheck = validateStructuredCommunication(reference)

  // One statement, one transaction.
  //
  // This used to be eight round trips: the extraction, the obligation, the
  // instalment, the routing, the supplier, the instalment again for space_id,
  // the pinned IBAN, and then two log rows. A closed tab anywhere in the
  // middle left a real state behind, and two of them were serious — an
  // obligation marked verified (so payable, so QR-able) with no record of who
  // verified it, and an obligation moved to another space while its
  // instalments stayed behind, which drops the item out of the payments list
  // entirely. See migration 0044.
  //
  // The validation stays here. IBAN mod-97, the structured communication and
  // the amount are deterministic and tested on this side, and rule 16 says
  // that is where those numbers come from. The database writes what it is
  // given — but it writes all of it or none of it.
  const patch: Record<string, string> = {}
  if (ibanCheck.valid) patch.iban = ibanCheck.canonical
  if (reference) {
    patch.payment_reference = refCheck.valid ? formatReference(reference) : reference
    if (refCheck.valid) patch.reference_canonical = refCheck.canonical
  }
  const amount = parseAmount(value('total_amount'))
  if (amount !== null) patch.total_amount_cents = String(amount)
  const due = value('due_date')
  if (/^\d{4}-\d{2}-\d{2}$/.test(due)) patch.due_date = due

  const routingPatch: Record<string, string | null> = {}
  if (routing?.spaceId) routingPatch.space_id = routing.spaceId
  if (routing && routing.category !== undefined) routingPatch.category = routing.category

  const { error } = await supabase.rpc('verify_document', {
    p_document_id: subject.documentId,
    p_extraction: {
      model_version: subject.modelVersion,
      lane_reasons: changed.length
        ? ['human_corrected', ...changed.map((c) => `fixed_${c}`)]
        : ['human_confirmed'],
      fields: finalFields,
    },
    p_obligation_id: subject.obligationId ?? null,
    p_party_id: subject.partyId ?? null,
    p_patch: patch,
    p_routing: routingPatch,
    p_pin_iban: ibanCheck.valid && subject.partyId ? ibanCheck.canonical : null,
    p_corrected: changed,
    p_iban_change_acknowledged: ibanChangeAcknowledged,
  })
  if (error) throw error
}


/**
 * What the admin typed, in cents.
 *
 * This used to be its own three-line parser, and the screen above it validated
 * with `parseAmountToCents`. The two disagreed about "1.289": the validator
 * read a thousands group and let the field pass, this one read a decimal point
 * and wrote 129 cents. The admin confirmed €1.289,00 and the ledger recorded
 * €1,29 — verified, so a QR code would have been generated from it.
 *
 * One parser. The field that was checked is the field that is written.
 */
export function parseAmount(value: string): number | null {
  if (!value) return null
  const c = parseAmountToCents(value)
  return c !== null && c >= 0 ? c : null
}
